import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthenticationFacade } from '@planone/bau365/data-access-authentication';
import {
  DataAccessEventsFacade,
  DataAccessExhibitorsFacade,
  DataAccessFairsFacade,
  DataAccessPostsFacade,
  DataAccessProductsFacade,
} from '@planone/bau365/data-access-fair-data';
import { ContactPerson, Contributor, Event, Exhibitor, Post, Product } from '@planone/bau365/util-fair-data';
import * as FeatureTrackingGoogleAnalytics from '@planone/shared/feature-tracking-google-analytics';
import * as FeatureTrackingSnowplow from '@planone/shared/feature-tracking-snowplow';
import { combineLatestWith, filter, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';

import * as ShareActions from './share.actions';
import {
  EventShareButtonClickEventInterface,
  EventShareDialogOpenEventInterface,
  ExhibitorShareButtonClickEventInterface,
  ExhibitorShareDialogOpenEventInterface,
  PostShareButtonClickEventInterface,
  PostShareDialogOpenEventInterface,
  ProductShareButtonClickEventInterface,
  ProductShareDialogOpenEventInterface,
} from './share.models';

import { DataTransformationHelper } from '../../helper/data-transformation.helpers';
import { CustomEventIglu, SocialNetworkMapping } from '../../tracking-types/snowplow/schema/schema.interface';

@Injectable()
export class ShareEffects {
  generalShareDialogOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareDialogOpened),
      filter(
        (action: any) =>
          action.entityType === undefined || action.entityType === 'GENERAL' || action.entityType === 'UNKNOWN'
      ),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$),
      switchMap(([action, exclude_analytics]) =>
        of(
          FeatureTrackingGoogleAnalytics.push({
            event: 'open_share_dialog',
            content_type: action.entityType,
            context: action.context,
            page_type: action.pageType,
            item_id: undefined,
            item_name: undefined,
            exclude_analytics: exclude_analytics,
          })
        )
      )
    )
  );

  eventShareDialogOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareDialogOpened),
      filter((action: any) => action.entityType === 'EVENT'),
      switchMap((action) => {
        return this.eventsFacade.event$.pipe(
          filter((event) => !!event || !!action.entities?.event),
          take(1),
          map((event) => [action, event])
        );
      }),
      map(([action, event]) => [action, event ?? action.entities?.event]),
      switchMap(([action, event]) =>
        of(<Event>event).pipe(
          combineLatestWith(this.exhibitorsFacade.getExhibitorById(event?.exhibitor_id)),
          filter(([_, exhibitor]) => !!exhibitor || !!action.entities?.exhibitor),
          map(([_, exhibitor]) => [event, exhibitor ?? action.entities?.exhibitor]),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$),
          map(
            ([[event, exhibitor], exclude_analytics]) =>
              <[EventShareDialogOpenEventInterface, Event, Exhibitor, boolean]>[
                action,
                event,
                exhibitor,
                exclude_analytics,
              ]
          ),
          switchMap(([action, event, exhibitor, exclude_analytics]) =>
            of(
              FeatureTrackingGoogleAnalytics.push({
                event: 'open_share_dialog',
                content_type: action.entityType,
                context: action.context,
                page_type: action.pageType,
                item_id: event.id,
                item_name: event.headline,
                exclude_analytics: exclude_analytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('EVENT', event),
              })
            )
          )
        )
      )
    )
  );

  contributorShareDialogOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareDialogOpened),
      filter((action: any) => action.entityType === 'CONTRIBUTOR'),
      switchMap((action) => {
        return this.eventsFacade.event$.pipe(
          filter((event) => !!event || !!action.entities?.event),
          take(1),
          map((event) => [action, event])
        );
      }),
      map(([action, event]) => [action, event ?? action.entities?.event]),
      switchMap(([action, event]) =>
        of(<Event>event).pipe(
          combineLatestWith(this.exhibitorsFacade.getExhibitorById(event?.exhibitor_id)),
          filter(([_, exhibitor]) => !!exhibitor || !!action.entities?.exhibitor),
          map(([_, exhibitor]) => [event, exhibitor ?? action.entities?.exhibitor]),
          combineLatestWith(this.eventsFacade.contributor$),
          filter(([[_, __], contributor]) => !!contributor || !!action.entities?.contributor),
          map(([[_, exhibitor], contributor]) => [[event, exhibitor], contributor ?? action.entities?.contributor]),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$),
          map(
            ([[[event, exhibitor], contributor], exclude_analytics]) =>
              <[EventShareDialogOpenEventInterface, Event, Contributor, Exhibitor, boolean]>[
                action,
                event,
                contributor,
                exhibitor,
                exclude_analytics,
              ]
          ),
          switchMap(([action, event, contributor, exhibitor, exclude_analytics]) =>
            of(
              FeatureTrackingGoogleAnalytics.push({
                event: 'open_share_dialog',
                content_type: action.entityType,
                context: action.context,
                page_type: action.pageType,
                item_id: contributor.id,
                item_name: 'Anonymized',
                exclude_analytics: exclude_analytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('EVENT', event),
                ...this.dataTransformationHelper.googleAnalytics?.map('CONTRIBUTOR', contributor),
              })
            )
          )
        )
      )
    )
  );

  exhibitorShareDialogOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareDialogOpened),
      filter((action: any) => action.entityType === 'EXHIBITOR' || action.entityType === 'BRAND'),
      switchMap((action) => {
        return this.exhibitorsFacade.exhibitor$.pipe(
          filter((exhibitor) => !!exhibitor || !!action.entities?.exhibitor),
          take(1),
          map((exhibitor) => [action, exhibitor])
        );
      }),
      map(([action, exhibitor]) => [action, exhibitor ?? action.entities?.exhibitor]),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$),
      map(
        ([[action, exhibitor], exclude_analytics]) =>
          <[ExhibitorShareDialogOpenEventInterface, Exhibitor, boolean]>[action, exhibitor, exclude_analytics]
      ),
      switchMap(([action, exhibitor, exclude_analytics]) =>
        of(
          FeatureTrackingGoogleAnalytics.push({
            event: 'open_share_dialog',
            content_type: action.entityType,
            context: action.context,
            page_type: action.pageType,
            item_id: exhibitor.id,
            item_name: exhibitor.display_name ?? exhibitor.name,
            exclude_analytics: exclude_analytics,
            ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
          })
        )
      )
    )
  );

  contactPersonShareDialogOpen$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ShareActions.shareDialogOpened),
        filter((action: any) => action.entityType === 'CONTACT_PERSON'),
        switchMap((action) => {
          return this.exhibitorsFacade.exhibitor$.pipe(
            filter((exhibitor) => !!exhibitor || !!action.entities?.exhibitor),
            take(1),
            map((exhibitor) => [action, exhibitor])
          );
        }),
        map(([action, exhibitor]) => [action, exhibitor ?? action.entities?.exhibitor]),
        withLatestFrom(this.exhibitorsFacade.exhibitorContactPerson$),
        filter(([[action, _], contactPerson]) => !!contactPerson || !!action.entities?.contact_person),
        map(([[action, exhibitor], contactPerson]) => [
          [action, exhibitor],
          contactPerson ?? action.entities?.contact_person,
        ]),
        withLatestFrom(this.authenticationFacade.excludeAnalytics$),
        map(
          ([[[action, exhibitor], contactPerson], exclude_analytics]) =>
            <[ExhibitorShareDialogOpenEventInterface, Exhibitor, ContactPerson, boolean]>[
              action,
              exhibitor,
              contactPerson,
              exclude_analytics,
            ]
        ),
        tap(([action, exhibitor, contactPerson, exclude_analytics]) =>
          this.store.dispatch(
            FeatureTrackingGoogleAnalytics.push({
              event: 'open_share_dialog',
              content_type: action.entityType,
              context: action.context,
              page_type: action.pageType,
              item_id: contactPerson.id,
              item_name: 'Anonymized',
              exclude_analytics: exclude_analytics,
              ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
              ...this.dataTransformationHelper.googleAnalytics?.map('CONTACT_PERSON', contactPerson),
            })
          )
        )
      ),
    { dispatch: false }
  );

  postShareDialogOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareDialogOpened),
      filter((action: any) => action.entityType === 'POST'),
      switchMap((action) => {
        return this.postsFacade.post$.pipe(
          filter((post) => !!post || !!action.entities?.post),
          take(1),
          map((post) => [action, post])
        );
      }),
      map(([action, post]) => [action, post ?? action.entities?.post]),
      switchMap(([action, post]) =>
        of(<Post>post).pipe(
          combineLatestWith(this.exhibitorsFacade.getExhibitorById(post?.exhibitor_id)),
          filter(([_, exhibitor]) => !!exhibitor || !!action.entities?.exhibitor),
          map(([_, exhibitor]) => [post, exhibitor ?? action.entities?.exhibitor]),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$),
          map(
            ([[post, exhibitor], exclude_analytics]) =>
              <[PostShareDialogOpenEventInterface, Post, Exhibitor, boolean]>[
                action,
                post,
                exhibitor,
                exclude_analytics,
              ]
          ),
          switchMap(([action, post, exhibitor, exclude_analytics]) =>
            of(
              FeatureTrackingGoogleAnalytics.push({
                event: 'open_share_dialog',
                content_type: action.entityType,
                context: action.context,
                page_type: action.pageType,
                item_id: post.id,
                item_name: post.headline,
                exclude_analytics: exclude_analytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('POST', post),
              })
            )
          )
        )
      )
    )
  );

  productShareDialogOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareDialogOpened),
      filter((action: any) => action.entityType === 'PRODUCT'),
      switchMap((action) => {
        return this.productsFacade.product$.pipe(
          filter((product) => !!product || !!action.entities?.product),
          take(1),
          map((product) => [action, product])
        );
      }),
      map(([action, product]) => [action, product ?? action.entities?.product]),
      switchMap(([action, product]) =>
        of(<Product>product).pipe(
          combineLatestWith(this.exhibitorsFacade.getExhibitorById(product?.exhibitor_id)),
          filter(([_, exhibitor]) => !!exhibitor || !!action.entities?.exhibitor),
          map(([_, exhibitor]) => [product, exhibitor ?? action.entities?.exhibitor]),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$),
          map(
            ([[product, exhibitor], exclude_analytics]) =>
              <[ProductShareDialogOpenEventInterface, Product, Exhibitor, boolean]>[
                action,
                product,
                exhibitor,
                exclude_analytics,
              ]
          ),
          switchMap(([action, product, exhibitor, exclude_analytics]) =>
            of(
              FeatureTrackingGoogleAnalytics.push({
                event: 'open_share_dialog',
                content_type: action.entityType,
                context: action.context,
                page_type: action.pageType,
                item_id: product.id,
                item_name: product.name,
                exclude_analytics: exclude_analytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('PRODUCT', product),
              })
            )
          )
        )
      )
    )
  );

  searchResultShareDialogOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareDialogOpened),
      filter(
        (action: any) =>
          action.entityType === 'EXHIBITOR_SEARCH_RESULT' ||
          action.entityType === 'BRAND_SEARCH_RESULT' ||
          action.entityType === 'SEARCH_RESULT'
      ),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$),
      switchMap(([action, exclude_analytics]) => {
        let contentType: string | undefined = undefined;

        switch (action.entityType) {
          case 'EXHIBITOR_SEARCH_RESULT':
            contentType = 'EXHIBITOR';
            break;
          case 'BRAND_SEARCH_RESULT':
            contentType = 'BRAND';
            break;
          case 'SEARCH_RESULT':
          default:
            contentType = action.entities.search_result.type;
            break;
        }

        return of(
          FeatureTrackingGoogleAnalytics.push({
            event: 'open_share_dialog',
            content_type: contentType,
            context: action.context,
            page_type: action.pageType,
            exclude_analytics: exclude_analytics,
            ...this.dataTransformationHelper.googleAnalytics?.map('SEARCH_RESULT', action.entities.search_result),
          })
        );
      })
    )
  );

  watchlistShareDialogOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.watchlistShareDialogOpened),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$),
      switchMap(([action, exclude_analytics]) => {
        return of(
          FeatureTrackingGoogleAnalytics.push({
            event: 'open_share_dialog',
            content_type: action.contentType,
            context: action.context,
            page_type: action.pageType,
            exclude_analytics: exclude_analytics,
            ...(action.contentType === 'PRODUCT' && {
              item_id: action.product?.id,
              item_name: action.product?.name,
            }),
            ...(action.contentType === 'POST' && {
              item_id: action.post?.id,
              item_name: action.post?.headline,
            }),
            ...(action.contentType === 'EXHIBITOR' && {
              item_id: action.exhibitor?.id,
              item_name: action.exhibitor?.name,
            }),
            ...(action.contentType === 'EVENT' && {
              item_id: action.event?.id,
              item_name: action.event?.headline,
            }),
            ...(action.contentType === 'BRAND' && {
              item_id: action.exhibitor?.id,
              item_name: action.exhibitor?.name,
            }),
            ...this.dataTransformationHelper.googleAnalytics?.map('WATCHLIST', action),
          })
        );
      })
    )
  );

  generalShareButtonClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareButtonClicked),
      filter(
        (action: any) =>
          action.entityType === undefined || action.entityType === 'GENERAL' || action.entityType === 'UNKNOWN'
      ),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$, this.fairsFacade.trackingFairId$),
      switchMap(([action, exclude_analytics, fairId]) =>
        of(
          FeatureTrackingSnowplow.selfDescribingEvent({
            event: {
              schema: CustomEventIglu.share,
              data: {
                method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
                entity_type: action.entityType,
                context: fairId,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
              },
            },
          }),
          FeatureTrackingGoogleAnalytics.push({
            event: 'perform_social_share',
            method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
            content_type: action.entityType,
            context: action.context,
            page_type: action.pageType,
            exclude_analytics: exclude_analytics,
          })
        )
      )
    )
  );

  eventShareButtonClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareButtonClicked),
      filter((action: any) => action.entityType === 'EVENT'),
      switchMap((action) => {
        return this.eventsFacade.event$.pipe(
          filter((event) => !!event || !!action.entities?.event),
          take(1),
          map((event) => [action, event])
        );
      }),
      map(([action, event]) => [action, event ?? action.entities?.event]),
      switchMap(([action, event]) =>
        of(<Event>event).pipe(
          combineLatestWith(this.exhibitorsFacade.getExhibitorById(event?.exhibitor_id)),
          filter(([_, exhibitor]) => !!exhibitor || !!action.entities?.exhibitor),
          map(([_, exhibitor]) => [event, exhibitor ?? action.entities?.exhibitor]),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$, this.fairsFacade.trackingFairId$),
          map(
            ([[event, exhibitor], exclude_analytics, fairId]) =>
              <[EventShareButtonClickEventInterface, Event, Exhibitor, boolean, string]>[
                action,
                event,
                exhibitor,
                exclude_analytics,
                fairId,
              ]
          ),
          switchMap(([action, event, exhibitor, exclude_analytics, fairId]) => {
            const extendedExhibitor = { ...exhibitor, fair_id: fairId === 'cross-fair' ? event.fair_id : fairId };
            const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map(
              'EXHIBITOR',
              extendedExhibitor
            );
            const snowplowEventContext = this.dataTransformationHelper.snowplow?.map('EVENT', event);

            const snowplowContext = [];

            if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
            if (snowplowEventContext) snowplowContext.push(snowplowEventContext);

            return of(
              FeatureTrackingSnowplow.selfDescribingEvent({
                event: {
                  schema: CustomEventIglu.share,
                  data: {
                    method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
                    entity_type: action.entityType,
                    context: fairId,
                    page_type: action.pageType,
                    exclude_analytics: exclude_analytics,
                  },
                },
                context: snowplowContext ? snowplowContext : undefined,
                enriched: true,
              }),
              FeatureTrackingGoogleAnalytics.push({
                event: 'perform_social_share',
                method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
                content_type: action.entityType,
                context: action.context,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('EVENT', event),
              })
            );
          })
        )
      )
    )
  );

  contributorShareButtonClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareButtonClicked),
      filter((action: any) => action.entityType === 'CONTRIBUTOR'),
      switchMap((action) => {
        return this.eventsFacade.event$.pipe(
          filter((event) => !!event || !!action.entities?.event),
          take(1),
          map((event) => [action, event])
        );
      }),
      map(([action, event]) => [action, event ?? action.entities?.event]),
      switchMap(([action, event]) =>
        of(<Event>event).pipe(
          combineLatestWith(this.exhibitorsFacade.getExhibitorById(event?.exhibitor_id)),
          filter(([_, exhibitor]) => !!exhibitor || !!action.entities?.exhibitor),
          map(([_, exhibitor]) => [event, exhibitor ?? action.entities?.exhibitor]),
          combineLatestWith(this.eventsFacade.contributor$),
          filter(([_, contributor]) => !!contributor || !!action.entities?.contributor),
          map(([[_, exhibitor], contributor]) => [[event, exhibitor], contributor ?? action.entities?.contributor]),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$, this.fairsFacade.trackingFairId$),
          map(
            ([[[event, exhibitor], contributor], exclude_analytics, fairId]) =>
              <[EventShareButtonClickEventInterface, Event, Contributor, Exhibitor, boolean, string]>[
                action,
                event,
                contributor,
                exhibitor,
                exclude_analytics,
                fairId,
              ]
          ),
          switchMap(([action, event, contributor, exhibitor, exclude_analytics, fairId]) => {
            const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);
            const snowplowEventContext = this.dataTransformationHelper.snowplow?.map('EVENT', event);
            const snowplowContributorContext = this.dataTransformationHelper.snowplow?.map(
              'CONTRIBUTOR',
              contributor,
              exhibitor.fair_id
            );

            const snowplowContext = [];

            if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
            if (snowplowEventContext) snowplowContext.push(snowplowEventContext);
            if (snowplowContributorContext) snowplowContext.push(snowplowContributorContext);

            return of(
              FeatureTrackingSnowplow.selfDescribingEvent({
                event: {
                  schema: CustomEventIglu.share,
                  data: {
                    method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
                    entity_type: action.entityType,
                    context: fairId,
                    page_type: action.pageType,
                    exclude_analytics: exclude_analytics,
                  },
                },
                context: snowplowContext ? snowplowContext : undefined,
                enriched: true,
              }),
              FeatureTrackingGoogleAnalytics.push({
                event: 'perform_social_share',
                method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
                content_type: action.entityType,
                context: action.context,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('EVENT', event),
                ...this.dataTransformationHelper.googleAnalytics?.map('CONTRIBUTOR', contributor),
              })
            );
          })
        )
      )
    )
  );

  exhibitorShareButtonClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareButtonClicked),
      filter((action: any) => action.entityType === 'EXHIBITOR' || action.entityType === 'BRAND'),
      switchMap((action) => {
        return this.exhibitorsFacade.exhibitor$.pipe(
          filter((exhibitor) => !!exhibitor || !!action.entities?.exhibitor),
          take(1),
          map((exhibitor) => [action, exhibitor])
        );
      }),
      map(([action, exhibitor]) => [action, exhibitor ?? action.entities?.exhibitor]),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$, this.fairsFacade.trackingFairId$),
      map(
        ([[action, exhibitor], exclude_analytics, fairId]) =>
          <[ExhibitorShareButtonClickEventInterface, Exhibitor, boolean, string]>[
            action,
            exhibitor,
            exclude_analytics,
            fairId,
          ]
      ),
      switchMap(([action, exhibitor, exclude_analytics, fairId]) => {
        const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);

        return of(
          FeatureTrackingSnowplow.selfDescribingEvent({
            event: {
              schema: CustomEventIglu.share,
              data: {
                method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
                entity_type: action.entityType,
                context: fairId,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
              },
            },
            context: snowplowExhibitorContext ? [snowplowExhibitorContext] : undefined,
            enriched: true,
          }),
          FeatureTrackingGoogleAnalytics.push({
            event: 'perform_social_share',
            method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
            content_type: action.entityType,
            context: action.context,
            page_type: action.pageType,
            exclude_analytics: exclude_analytics,
            ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
          })
        );
      })
    )
  );

  contactPersonShareButtonClick$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ShareActions.shareButtonClicked),
        filter((action: any) => action.entityType === 'CONTACT_PERSON'),
        switchMap((action) => {
          return this.exhibitorsFacade.exhibitor$.pipe(
            filter((exhibitor) => !!exhibitor || !!action.entities?.exhibitor),
            take(1),
            map((exhibitor) => [action, exhibitor])
          );
        }),
        map(([action, exhibitor]) => [action, exhibitor ?? action.entities?.exhibitor]),
        withLatestFrom(this.exhibitorsFacade.exhibitorContactPerson$),
        filter(([[action, _], contactPerson]) => !!contactPerson || !!action.entities?.contact_person),
        map(([[action, exhibitor], contactPerson]) => [
          [action, exhibitor],
          contactPerson ?? action.entities?.contact_person,
        ]),
        withLatestFrom(this.authenticationFacade.excludeAnalytics$, this.fairsFacade.trackingFairId$),
        map(
          ([[[action, exhibitor], contactPerson], exclude_analytics, fairId]) =>
            <[ExhibitorShareButtonClickEventInterface, Exhibitor, ContactPerson, boolean, string]>[
              action,
              exhibitor,
              contactPerson,
              exclude_analytics,
              fairId,
            ]
        ),
        tap(([action, exhibitor, contactPerson, exclude_analytics, fairId]) => {
          const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);
          const snowplowContactPersonContext = this.dataTransformationHelper.snowplow?.map(
            'CONTACT_PERSON',
            contactPerson,
            action.context
          );

          const snowplowContext = [];

          if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
          if (snowplowContactPersonContext) snowplowContext.push(snowplowContactPersonContext);

          this.store.dispatch(
            FeatureTrackingSnowplow.selfDescribingEvent({
              event: {
                schema: CustomEventIglu.share,
                data: {
                  method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
                  entity_type: action.entityType,
                  context: fairId,
                  page_type: action.pageType,
                  exclude_analytics: exclude_analytics,
                },
              },
              context: snowplowContext ? snowplowContext : undefined,
              enriched: true,
            })
          );

          this.store.dispatch(
            FeatureTrackingGoogleAnalytics.push({
              event: 'perform_social_share',
              method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
              content_type: action.entityType,
              context: action.context,
              page_type: action.pageType,
              exclude_analytics: exclude_analytics,
              ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
              ...this.dataTransformationHelper.googleAnalytics?.map('CONTACT_PERSON', contactPerson),
            })
          );
        })
      ),
    { dispatch: false }
  );

  postShareButtonClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareButtonClicked),
      filter((action: any) => action.entityType === 'POST'),
      switchMap((action) => {
        return this.postsFacade.post$.pipe(
          filter((post) => !!post || !!action.entities?.post),
          take(1),
          map((post) => [action, post])
        );
      }),
      map(([action, post]) => [action, post ?? action.entities?.post]),
      switchMap(([action, post]) =>
        of(<Post>post).pipe(
          combineLatestWith(this.exhibitorsFacade.getExhibitorById(post?.exhibitor_id)),
          filter(([_, exhibitor]) => !!exhibitor || !!action.entities?.exhibitor),
          map(([_, exhibitor]) => [post, exhibitor ?? action.entities?.exhibitor]),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$, this.fairsFacade.trackingFairId$),
          map(
            ([[post, exhibitor], exclude_analytics, fairId]) =>
              <[PostShareButtonClickEventInterface, Post, Exhibitor, boolean, string]>[
                action,
                post,
                exhibitor,
                exclude_analytics,
                fairId,
              ]
          ),
          switchMap(([action, post, exhibitor, exclude_analytics, fairId]) => {
            const extendedExhibitor = { ...exhibitor, fair_id: fairId === 'cross-fair' ? post.fair_id : fairId };
            const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map(
              'EXHIBITOR',
              extendedExhibitor
            );
            const snowplowPostContext = this.dataTransformationHelper.snowplow?.map('POST', post);
            const snowplowContext = [];

            if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
            if (snowplowPostContext) snowplowContext.push(snowplowPostContext);

            return of(
              FeatureTrackingSnowplow.selfDescribingEvent({
                event: {
                  schema: CustomEventIglu.share,
                  data: {
                    method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
                    entity_type: action.entityType,
                    context: fairId,
                    page_type: action.pageType,
                    exclude_analytics: exclude_analytics,
                  },
                },
                context: snowplowContext ? snowplowContext : undefined,
                enriched: true,
              }),
              FeatureTrackingGoogleAnalytics.push({
                event: 'perform_social_share',
                method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
                content_type: action.entityType,
                context: action.context,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('POST', post),
              })
            );
          })
        )
      )
    )
  );

  productShareButtonClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareButtonClicked),
      filter((action: any) => action.entityType === 'PRODUCT'),
      switchMap((action) => {
        return this.productsFacade.product$.pipe(
          filter((product) => !!product || !!action.entities?.product),
          take(1),
          map((product) => [action, product])
        );
      }),
      map(([action, product]) => [action, product ?? action.entities?.product]),
      switchMap(([action, product]) =>
        of(<Product>product).pipe(
          combineLatestWith(this.exhibitorsFacade.getExhibitorById(product?.exhibitor_id)),
          filter(([_, exhibitor]) => !!exhibitor || !!action.entities?.exhibitor),
          map(([_, exhibitor]) => [product, exhibitor ?? action.entities?.exhibitor]),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$, this.fairsFacade.trackingFairId$),
          map(
            ([[product, exhibitor], exclude_analytics, fairId]) =>
              <[ProductShareButtonClickEventInterface, Product, Exhibitor, boolean, string]>[
                action,
                product,
                exhibitor,
                exclude_analytics,
                fairId,
              ]
          ),
          switchMap(([action, product, exhibitor, exclude_analytics, fairId]) => {
            const extendedExhibitor = { ...exhibitor, fair_id: fairId === 'cross-fair' ? product.fair_id : fairId };
            const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map(
              'EXHIBITOR',
              extendedExhibitor
            );
            const snowplowProductContext = this.dataTransformationHelper.snowplow?.map(
              'PRODUCT',
              product,
              extendedExhibitor.fair_id
            );

            const snowplowContext = [];

            if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
            if (snowplowProductContext) snowplowContext.push(snowplowProductContext);

            return of(
              FeatureTrackingSnowplow.selfDescribingEvent({
                event: {
                  schema: CustomEventIglu.share,
                  data: {
                    method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
                    entity_type: action.entityType,
                    context: fairId,
                    page_type: action.pageType,
                    exclude_analytics: exclude_analytics,
                  },
                },
                context: snowplowContext ? snowplowContext : undefined,
                enriched: true,
              }),
              FeatureTrackingGoogleAnalytics.push({
                event: 'perform_social_share',
                method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
                content_type: action.entityType,
                context: action.context,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('PRODUCT', product),
              })
            );
          })
        )
      )
    )
  );

  searchResultShareButtonClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareButtonClicked),
      filter(
        (action: any) =>
          action.entityType === 'EXHIBITOR_SEARCH_RESULT' ||
          action.entityType === 'BRAND_SEARCH_RESULT' ||
          action.entityType === 'SEARCH_RESULT'
      ),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$, this.fairsFacade.trackingFairId$),
      switchMap(([action, exclude_analytics, fairId]) => {
        let contentType: string | undefined = undefined;

        switch (action.entityType) {
          case 'EXHIBITOR_SEARCH_RESULT':
            contentType = 'EXHIBITOR';
            break;
          case 'BRAND_SEARCH_RESULT':
            contentType = 'BRAND';
            break;
          case 'SEARCH_RESULT':
          default:
            contentType = action.entities.search_result.type;
            break;
        }

        const snowplowSearchResultContext = this.dataTransformationHelper.snowplow?.mapArray(
          action.entityType,
          action.entities.search_result,
          action.context
        );

        return of(
          FeatureTrackingSnowplow.selfDescribingEvent({
            event: {
              schema: CustomEventIglu.share,
              data: {
                method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
                entity_type: action.entities.search_result.type,
                context: fairId,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
              },
            },
            context: snowplowSearchResultContext ? snowplowSearchResultContext : undefined,
            enriched: true,
          }),
          FeatureTrackingGoogleAnalytics.push({
            event: 'perform_social_share',
            method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
            content_type: contentType,
            context: action.context,
            page_type: action.pageType,
            exclude_analytics: exclude_analytics,
            ...this.dataTransformationHelper.googleAnalytics?.map(action.entityType, action.entities.search_result),
          })
        );
      })
    )
  );

  watchlistShareButtonClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.watchlistShareButtonClicked),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$, this.fairsFacade.trackingFairId$),
      switchMap(([action, exclude_analytics, fairId]) => {
        const snowplowShareButtonClickContext = this.dataTransformationHelper.snowplow?.mapArray(
          'WATCHLIST',
          action.change
        );
        console.log('watchlistShareButtonClick$: ', snowplowShareButtonClickContext);
        return of(
          FeatureTrackingSnowplow.selfDescribingEvent({
            event: {
              schema: CustomEventIglu.share,
              data: {
                method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
                entity_type: action.change.contentType,
                context: fairId,
                page_type: action.change.pageType,
                exclude_analytics: exclude_analytics,
              },
            },
            context: snowplowShareButtonClickContext ? snowplowShareButtonClickContext : undefined,
            enriched: true,
          }),
          FeatureTrackingGoogleAnalytics.push({
            event: 'perform_social_share',
            method: SocialNetworkMapping[action.network as keyof typeof SocialNetworkMapping],
            content_type: action.change.contentType,
            context: action.change.context,
            page_type: action.change.pageType,
            exclude_analytics: exclude_analytics,
            ...(action.change.contentType === 'PRODUCT' && {
              item_id: action.change.product?.id,
              item_name: action.change.product?.name,
            }),
            ...(action.change.contentType === 'POST' && {
              item_id: action.change.post?.id,
              item_name: action.change.post?.headline,
            }),
            ...(action.change.contentType === 'EXHIBITOR' && {
              item_id: action.change.exhibitor?.id,
              item_name: action.change.exhibitor?.name,
            }),
            ...(action.change.contentType === 'EVENT' && {
              item_id: action.change.event?.id,
              item_name: action.change.event?.headline,
            }),
            ...(action.change.contentType === 'BRAND' && {
              item_id: action.change.brand?.id,
              item_name: action.change.brand?.name,
            }),
            ...this.dataTransformationHelper.googleAnalytics?.map('WATCHLIST', action.change),
          })
        );
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private store: Store,
    private authenticationFacade: AuthenticationFacade,
    private exhibitorsFacade: DataAccessExhibitorsFacade,
    private eventsFacade: DataAccessEventsFacade,
    private postsFacade: DataAccessPostsFacade,
    private productsFacade: DataAccessProductsFacade,
    private fairsFacade: DataAccessFairsFacade,
    private dataTransformationHelper: DataTransformationHelper
  ) {}
}
